import { ROUTES } from '../../../../constants';
import { Attachment, AttachmentType } from '../../../../types';
import { logger } from '../../../util/logger';
import { httpClient } from '../../httpClient';
import { BaseMediaUploader, BaseUploadUrlResponse } from './baseMediaUploader';
import { resolveUploadMimeType, SUPPORTED_AI_GATEWAY_IMAGE_FILES } from './constants';
import { MediaProbeResult } from './probeMediaDimensions';

// Images only — routed here when the field's attachFile is false (see convertors/index.ts).
// Videos never reach this uploader; they always go through the Dynamics/legacy path.

export class WixmpMediaUploader extends BaseMediaUploader<BaseUploadUrlResponse> {
  protected readonly requiredResponseFields = ['uploadUrl', 'attachmentUrl'] as const;
  static readonly uploaderName = 'WixmpMediaUploader';
  // AI gateway supports a narrower set than the media platform; drag-and-drop bypasses the picker's
  // `accept` filter, so validate here to reject e.g. .webp/.bmp/.tiff before hitting the gateway.
  static readonly supportedFileExtensions = SUPPORTED_AI_GATEWAY_IMAGE_FILES;

  protected async getUploadUrl(): Promise<BaseUploadUrlResponse> {
    try {
      const { data } = await httpClient
        .getInstance()
        .post<BaseUploadUrlResponse>(ROUTES.WIXMP_UPLOAD_URL_API, {
          fileName: this.file.name,
          mimeType: resolveUploadMimeType(this.file),
        });
      return data;
    } catch (error) {
      logger.error({ message: 'failed to get wixmp upload url', error });
      throw error;
    }
  }

  protected buildAttachment(response: BaseUploadUrlResponse, probe: MediaProbeResult): Attachment {
    return {
      type: AttachmentType.Image,
      name: this.file.name,
      size: this.file.size,
      width: probe.width,
      height: probe.height,
      url: response.attachmentUrl,
      attachingMethod: this.attachingMethod,
      // Use the resolved MIME (same as the BFF handshake and PUT) — file.type is empty for
      // extensions the OS doesn't map, which would leave the attachment with mimeType '' and later
      // fall back to application/octet-stream in DynamicsAttachmentTransfer (and risk being dropped).
      mimeType: resolveUploadMimeType(this.file),
    };
  }
}
